import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CheckCircle2, XCircle } from "lucide-react";

// Simulação final aprovada em 02/11/2026 (Produção, versão d8e70c47).
export const APROVADO = {
  total_registros: 388,
  reservas_r1_ids: ["548a23ed-5ae6-44fa-b604-d3934f15eff4", "7a3becfd-95c7-4ac0-864b-a90e3ef490d8"],
  contas_a_excluir: [
    { id: "4921444f-0fdc-4c0b-86e5-23130e998fae", nome: "PagBank – Conta de Pagamento" },
    { id: "53992423-34bb-45b6-8ca9-c4db185816b3", nome: "Sicredi - Conta Corporativa" },
  ],
  arquivos_a_apagar: [
    "rede-hospeda/extratos_bancarios/3bea8bd4a66143379b40cec131d51a83.ofx",
    "rede-hospeda/extratos_bancarios/4b1216f3a8d647e59683ef05e84f924e.ofx",
    "rede-hospeda/extratos_bancarios/4c89c2adb6b24120a8ed255c47c5c117.csv",
  ],
};

const igual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

function verificacoes(sim, status) {
  const ap = sim.aprovacao || {};
  return [
    ["Reset ainda não executado", status?.status === "nao_executado" && !sim.ja_executado],
    ["388 registros financeiros", ap.total_registros === APROVADO.total_registros],
    ["Reservas 548a23ed e 7a3becfd (IDs completos)", igual(ap.reservas_r1_ids, APROVADO.reservas_r1_ids)],
    ["Contas Sicredi e PagBank (IDs e nomes exatos)", igual(ap.contas_a_excluir, APROVADO.contas_a_excluir)],
    ["3 arquivos de extrato (caminhos exatos)", igual(ap.arquivos_a_apagar, APROVADO.arquivos_a_apagar)],
    ["Nenhuma reserva futura a preservar", sim.reservas_a_partir_abertura === 0],
    ["Plano de contas com 34 itens", sim.plano_de_contas_preservado === 34],
  ];
}

function Resultado({ out }) {
  return (
    <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm space-y-1" data-testid="reset-resultado">
      <div><b>Execução:</b> <span className="font-mono">{out.execucao_id}</span></div>
      <div><b>Cópia técnica válida até:</b> {out.backup_expira_em}</div>
      <div><b>Reservas ajustadas:</b> {out.reservas_ajustadas} · <b>Preservação idêntica:</b> {String(out.preservacao_identica)}</div>
      <div><b>Excluídos:</b> {Object.values(out.excluidos || {}).reduce((a, b) => a + b, 0)}</div>
      <ul className="list-disc pl-5">
        {(out.arquivos || []).map((a) => <li key={a.caminho} className="break-all">{a.arquivo_nome}: <b>{a.resultado}</b></li>)}
      </ul>
    </div>
  );
}

export function ExecutarReset({ sim, status, onConcluido }) {
  const [frase, setFrase] = useState("");
  const [aberto, setAberto] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [out, setOut] = useState(null);
  const checks = verificacoes(sim, status);
  const tudoOk = checks.every(([, ok]) => ok);
  const fraseOk = frase === sim.frase_confirmacao;

  const executar = async () => {
    setEnviando(true);
    setErro("");
    try {
      const r = await api.post("/admin/reset-financeiro/executar", {
        confirmacao: frase, assinatura: sim.assinatura, esperado: APROVADO,
      });
      setOut(r.data);
      onConcluido?.();
    } catch (e) {
      setErro(e?.response?.data?.detail || "Execução recusada. Nada foi alterado.");
    } finally {
      setEnviando(false);
      setAberto(false);
    }
  };

  return (
    <div className="bg-white border-2 border-red-200 rounded-lg p-6 mb-8" data-testid="reset-executar-painel">
      <h2 className="text-base md:text-lg font-semibold text-stone-900 mb-3">Executar reset (simulação final aprovada)</h2>
      <ul className="space-y-1 mb-4 text-sm" data-testid="reset-verificacoes">
        {checks.map(([nome, ok]) => (
          <li key={nome} className="flex items-center gap-2" data-testid={`reset-check-${ok ? "ok" : "falha"}`}>
            {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-red-600" />}{nome}
          </li>
        ))}
      </ul>
      {!tudoOk && !out && <p className="text-sm text-red-700 mb-3" data-testid="reset-bloqueado">Dados diferentes da simulação aprovada: execução bloqueada.</p>}
      {!out && (
        <div className="flex flex-col md:flex-row gap-3 md:items-center">
          <Input value={frase} onChange={(e) => setFrase(e.target.value)} placeholder={`Digite: ${sim.frase_confirmacao}`}
            disabled={!tudoOk} className="md:max-w-md" data-testid="reset-frase-input" />
          <Button variant="destructive" disabled={!tudoOk || !fraseOk || enviando} onClick={() => setAberto(true)} data-testid="reset-executar-btn">
            {enviando ? "Executando…" : "Executar reset financeiro"}
          </Button>
        </div>
      )}
      {erro && <p className="text-sm text-red-700 mt-3" data-testid="reset-executar-erro">{erro}</p>}
      {out && <Resultado out={out} />}
      <AlertDialog open={aberto} onOpenChange={setAberto}>
        <AlertDialogContent data-testid="reset-confirmar-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar o reset financeiro?</AlertDialogTitle>
            <AlertDialogDescription>
              Exclui 388 registros, as contas Sicredi e PagBank e os 3 arquivos de extrato, e neutraliza as reservas 548a23ed e 7a3becfd.
              Executa uma única vez. Os arquivos não voltam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-confirmar-cancelar">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={executar} className="bg-red-600 hover:bg-red-700" data-testid="reset-confirmar-executar">Executar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
